import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { formatDateID } from "@/lib/format";

const EMPTY = { title: "", excerpt: "", content: "", category: "Kegiatan", image_url: "", published: true };
const CATS = ["Kegiatan", "Prestasi", "Komunitas", "Tips"];
const inputCls = "border-white/10 bg-[#0B0D15] text-slate-200 placeholder:text-slate-500";

export const NewsTab = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => api.get("/admin/news").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (n) => {
    setEditing(n);
    setForm({ title: n.title, excerpt: n.excerpt, content: n.content, category: n.category, image_url: n.image_url, published: n.published });
    setOpen(true);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("Ukuran file melebihi 50 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);

    try {
      const res = await api.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, image_url: res.data.url }));
      toast.success("Gambar berita berhasil diunggah");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/news/${editing.id}`, form);
      else await api.post("/admin/news", form);
      toast.success(editing ? "Berita diperbarui" : "Berita ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Hapus berita "${n.title}"?`)) return;
    await api.delete(`/admin/news/${n.id}`);
    toast.success("Berita dihapus");
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-news-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-bold uppercase text-white">Kelola Berita</h2>
        <Button data-testid="admin-add-news-button" onClick={openNew} className="bg-rose-600 text-white hover:bg-rose-500">
          <Plus className="mr-2 h-4 w-4" /> Tambah Berita
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-white/10 bg-[#12141C]/90">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Judul</TableHead>
              <TableHead className="text-slate-400">Kategori</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Tanggal</TableHead>
              <TableHead className="text-right text-slate-400">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((n) => (
              <TableRow key={n.id} data-testid={`admin-news-row-${n.id}`} className="border-white/10">
                <TableCell className="max-w-xs truncate font-medium text-slate-200">{n.title}</TableCell>
                <TableCell className="text-slate-400">{n.category}</TableCell>
                <TableCell>
                  <span className={`rounded-md border px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest ${
                    n.published ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" : "border-white/15 bg-slate-800/60 text-slate-400"}`}>
                    {n.published ? "Tayang" : "Draft"}
                  </span>
                </TableCell>
                <TableCell className="text-slate-400">{formatDateID(n.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button data-testid={`admin-news-edit-${n.id}`} size="icon" variant="ghost" onClick={() => openEdit(n)}
                    className="text-slate-400 hover:text-amber-400"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`admin-news-delete-${n.id}`} size="icon" variant="ghost" onClick={() => remove(n)}
                    className="text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow className="border-white/10"><TableCell colSpan={5} className="py-8 text-center text-slate-500">Belum ada berita.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-white/10 bg-[#11131F]" data-testid="admin-news-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase text-white">{editing ? "Edit Berita" : "Tambah Berita"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Judul *</Label>
              <Input data-testid="news-form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Kategori</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="news-form-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#11131F] text-slate-200">
                  {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Gambar dari Perangkat</Label>
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="group relative flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-amber-500/40 bg-[#0B0D15] p-4 text-center transition-all hover:border-amber-400 hover:bg-[#0E111C]"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {uploading ? (
                  <div className="flex flex-col items-center py-2 text-rose-400">
                    <Loader2 className="mb-2 h-6 w-6 animate-spin" />
                    <span className="text-sm font-medium">Mengunggah gambar...</span>
                  </div>
                ) : (
                  <>
                    <div className="mb-1.5 flex items-center justify-center gap-2 text-amber-400 group-hover:text-amber-300">
                      <Upload className="h-4 w-4" />
                      <span className="text-sm font-semibold">Upload Gambar dari Perangkat</span>
                    </div>
                    <p className="text-[11px] text-slate-400">
                      JPG, PNG, WebP, GIF — maksimal 50 MB.
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="relative my-0.5 text-center">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <span className="relative bg-[#11131F] px-3 font-mono text-[11px] text-slate-500">
                atau tempel link
              </span>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">URL Gambar</Label>
              <Input data-testid="news-form-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className={inputCls} />
            </div>

            {form.image_url && <img src={form.image_url} alt="Pratinjau Gambar Berita" className="h-40 w-full rounded-lg border border-white/10 object-cover" />}

            <div className="space-y-2">
              <Label className="text-slate-300">Ringkasan</Label>
              <Textarea data-testid="news-form-excerpt-input" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Isi Berita</Label>
              <Textarea data-testid="news-form-content-input" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={inputCls} />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="news-form-published-switch" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
              <Label className="text-slate-300">Tayangkan di website</Label>
            </div>
            <Button data-testid="news-form-save-button" onClick={save} disabled={saving || uploading || !form.title.trim()}
              className="bg-rose-600 font-bold text-white hover:bg-rose-500">
              {saving ? "Menyimpan..." : "Simpan Berita"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

